import React, { FC, useContext, useState } from 'react';
import { GiftedChatMessage } from '../../../../components/DiscussionMessages/DiscussionMessagesComponent';
import { formatDate, formatName } from '../../../../utils/common';
import { Box, Typography } from '@material-ui/core';
import { INFO } from '../../../../constants/colors';
import { IUserContext, UserContext } from '../../../../providers/UserProvider';
import { Bubble, BubbleProps } from 'react-web-gifted-chat';
import ChatMenu from '../ChatMenu/ChatMenu';
import { useMutation } from '@apollo/react-hooks';
import {
  DeleteMessageInDiscussion,
  DeleteMessageInDiscussionVariables,
} from '../../../../graphql/discussion/types/DeleteMessageInDiscussion';
import { DELETE_MESSAGE_IN_DISCUSSION } from '../../../../graphql/discussion/mutation';
import ConfirmDialog from '../../../ConfirmDialog';
import { DiscussionMessageTag } from '../../../constants';
import useStyles from './styles';
import { useTheme } from '@material-ui/core/styles';
import {
  DiscussionMessages,
  DiscussionMessagesVariables,
} from '../../../../graphql/discussion/types/DiscussionMessages';
import {
  GET_DISCUSSION_MESSAGE,
  GET_DISCUSSIONS,
} from '../../../../graphql/discussion/query';
import {
  Discussions,
  DiscussionsVariables,
} from '../../../../graphql/discussion/types/Discussions';

interface ICustomBubble {
  onRespond?: (message: GiftedChatMessage | undefined) => void;
  disabled?: boolean;
}

const Header: FC<BubbleProps<GiftedChatMessage>> = (_) => {
  if (_?.currentMessage?.tags?.includes(DiscussionMessageTag.BUSINESS)) {
    const fullName = formatName({
      firstName: _?.currentMessage?.business?.firstName ?? '',
      lastName: _?.currentMessage?.business?.lastName ?? '',
    });
    return <HeaderContent title="Dossier" subtitle={fullName} />;
  }

  if (_?.currentMessage?.isQuotation) {
    const date = formatDate(
      new Date(_?.currentMessage?.quotedFrom?.createdAt ?? undefined),
    );
    return (
      <HeaderContent
        title={
          _?.currentMessage?.quotedFrom?.tags?.includes(
            DiscussionMessageTag.FEEDBACK,
          ) && 'Feedback'
        }
        subtitle={`Date : ${date.toString()}`}
        desc={_?.currentMessage?.quotedFrom?.content?.text}
      />
    );
  }

  return null;
};

export const HeaderContent = ({ title, subtitle, desc }: any) => {
  const classes = useStyles();
  return (
    <Box className={classes.header}>
      {!!title && (
        <Typography className={classes.headerTitle}>{title}</Typography>
      )}
      {!!subtitle && (
        <Typography className={classes.headerSubtitle}>{subtitle}</Typography>
      )}
      {!!desc && (
        <Typography className={classes.headerSubtitle}>{desc}</Typography>
      )}
    </Box>
  );
};

const CustomBubble: FC<ICustomBubble & BubbleProps<GiftedChatMessage>> = (
  props,
) => {
  const { currentMessage, onRespond, disabled } = props;
  const classes = useStyles();
  const theme = useTheme();

  const {
    me: { id: meId },
  } = useContext<IUserContext>(UserContext);

  const [confirmationVisible, setConfirmationVisible] = useState<boolean>(
    false,
  );

  const [deleteMessage, { loading }] = useMutation<
    DeleteMessageInDiscussion,
    DeleteMessageInDiscussionVariables
  >(DELETE_MESSAGE_IN_DISCUSSION, {
    update: (client, { data }) => {
      if (!data?.deleteMessageInDiscussion) return;
      setConfirmationVisible(false);

      // Update discussion messages
      try {
        const currentDiscussionMessages = client.readQuery<
          DiscussionMessages,
          DiscussionMessagesVariables
        >({
          query: GET_DISCUSSION_MESSAGE,
          variables: {
            input: {
              discussionId: currentMessage?.discussionId,
            },
          },
        });

        const filteredDiscussionMessages =
          currentDiscussionMessages?.discussionMessages?.messages?.filter(
            (message) => message?.id !== data?.deleteMessageInDiscussion?.id,
          ) ?? [];

        client.writeQuery<DiscussionMessages, DiscussionMessagesVariables>({
          query: GET_DISCUSSION_MESSAGE,
          variables: {
            input: {
              discussionId: currentMessage?.discussionId,
            },
          },
          data: Object.assign({}, currentDiscussionMessages, {
            discussionMessages: {
              ...currentDiscussionMessages?.discussionMessages,
              messages: filteredDiscussionMessages,
              totalCount: filteredDiscussionMessages?.length,
            },
          }),
        });
      } catch (e) {}

      // Update discussions
      try {
        const currentDiscussions = client.readQuery<
          Discussions,
          DiscussionsVariables
        >({
          query: GET_DISCUSSIONS,
        });

        client.writeQuery<Discussions, DiscussionsVariables>({
          query: GET_DISCUSSIONS,
          data: Object.assign({}, currentDiscussions, {
            discussions: {
              ...currentDiscussions,
              discussions: currentDiscussions?.discussions?.discussions?.map(
                (discussion) =>
                  discussion?.lastMessage?.id ===
                  data?.deleteMessageInDiscussion?.id
                    ? {
                        ...discussion,
                        lastMessage: {
                          ...discussion?.lastMessage,
                          isSeenByMe: true,
                          content: {
                            ...discussion?.lastMessage?.content,
                            text: 'Message supprimé',
                            uri: null,
                          },
                        },
                      }
                    : discussion,
              ),
            },
          }),
        });
      } catch (e) {}
    },
    onError: () => {
      // TODO: implement me
    },
  });

  const handleRespond = () => onRespond && onRespond(currentMessage);

  const handleDelete = () => setConfirmationVisible(true);

  const handleNoButton = () => setConfirmationVisible(false);

  const handleYesButton = () =>
    currentMessage?.id &&
    deleteMessage({ variables: { id: Number(currentMessage.id) } });

  return (
    <>
      <Box className={classes.wrapper}>
        <Bubble
          {...props}
          renderCustomView={Header}
          wrapperStyle={{
            left: {
              backgroundColor: '#E2E6EA',
              overflow: 'hidden',
              marginRight: theme.spacing(1),
            },
            right: {
              backgroundColor: INFO,
              overflow: 'hidden',
              marginRight: theme.spacing(1),
            },
          }}
        />
        <ChatMenu
          disabled={disabled}
          {...(currentMessage?.user?.id === meId
            ? { onDelete: handleDelete }
            : { onRespond: handleRespond })}
        />
      </Box>
      <ConfirmDialog
        title="Suppression du message"
        open={confirmationVisible}
        handleCancel={handleNoButton}
        handleOK={handleYesButton}
        loading={loading}
      />
    </>
  );
};

export default CustomBubble;
